import React, { useCallback } from 'react';
import {
  Modal,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@material-ui/core';
import { Button } from '../atoms/ButtonWithAuth';
import { IconButton } from '../atoms/IconButtonWithAuth';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Close, GetApp, Warning } from '@material-ui/icons';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import jaLocale from 'date-fns/locale/ja';
import KeyboardDatePicker from '../atoms/DatePickerWithAuth';
import { ServicerType } from '../../enums/common/servicer-type';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    container: {
      background: 'var(--color-white)',
      borderRadius: 10,
      textAlign: 'left',
      width: 460,
      position: 'relative'
    },
    closeButton: {
      color: 'var(--color-white)',
      position: 'absolute',
      fontSize: 20,
      fontWeight: 900,
      top: 10,
      right: 16
    },
    heading: {
      borderRadius: '10px 10px 0 0',
      color: 'var(--color-white)',
      backgroundColor: 'var(--color-admin-key)',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '29px',
      letterSpacing: '0.5px',
      padding: '20px'
    },
    submitButton: {
      backgroundColor: '#EB5757',
      borderRadius: 999,
      padding: '8px 20px',
      color: 'var(--color-white)',
      '&:hover': {
        backgroundColor: '#ce4040'
      }
    },
    root: {
      width: 'calc(100% - 40px)',
      padding: '0 20px 20px 20px'
    },
    listItem: {
      lineHeight: '44px'
    },
    targetPeriodContainer: {
      background: 'var(--color-white)',
      textAlign: 'left'
    },
    targetPeriodLabel: {
      fontSize: 12,
      color: 'var(--color-gray-3)',
      fontWeight: 500
    },
    button: {
      backgroundColor: '#E0E0E0',
      borderRadius: 999,
      marginLeft: 20,
      padding: '8px 12px 8px'
    },
    buttonsContainer: {
      marginTop: 12,
      textAlign: 'right'
    },
    cancelButton: {
      borderRadius: 999,
      padding: '8px 20px',
      color: 'var(--color-gray-1)',
      backgroundColor: 'var(--color-white)',
      marginRight: 16,
      '&:hover': {
        backgroundColor: '#eee'
      }
    },
    progress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translateY(-50%) translateX(-50%)'
    },
    disabledLink: {
      textDecoration: 'none'
    },
    error: {
      color: '#f44336',
      margin: 0,
      fontSize: '0.75rem',
      minHeight: '1em',
      textAlign: 'left',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: '1em',
      letterSpacing: '0.03333em'
    }
  })
);

interface Props {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  onClickNewData: (e: any) => void;
  onClickModifyData: (e: any) => void;
  startDate: string;
  endDate: string;
  setStartDate: (e: any) => void;
  setEndDate: (e: any) => void;
  isError: boolean;
  setIsError: (e: any) => void;
  processing: boolean;
  setProcessing: (value: boolean) => void;
  targetService: ServicerType;
}

const CsvExportModal: React.FC<Props> = props => {
  const {
    isOpen,
    setIsOpen,
    onClickNewData,
    onClickModifyData,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    isError,
    setIsError,
    processing,
    setProcessing
  } = props;

  const styles = useStyles({});
  const [minDate, setMinDate] = React.useState<string>('');
  const [maxDate, setMaxDate] = React.useState<string>('');
  const [startDateValid, setStartDateValid] = React.useState(false);
  const [endDateValid, setEndDateValid] = React.useState(false);

  // 開始日選択時の処理
  const onSelectStartDate = React.useCallback(
    (date: MaterialUiPickersDate) => {
      setIsError(false);
      setEndDateValid(false);
      if (!date || !moment(date).isValid()) {
        setStartDate('');
        setStartDateValid(false);
        return;
      }
      const formattedStart = moment(date).format('YYYY-MM-DD');
      setStartDate(formattedStart);
      // エラー判定: 開始日 > 終了日
      setStartDateValid(moment(formattedStart).isAfter(moment(endDate)));
    },
    [endDate, setStartDate, setIsError]
  );
  // 終了日選択時の処理
  const onSelectEndDate = React.useCallback(
    (date: MaterialUiPickersDate) => {
      setIsError(false);
      setStartDateValid(false);
      if (!date || !moment(date).isValid()) {
        setEndDate('');
        setEndDateValid(false);
        return;
      }
      const formattedEnd = moment(date).format('YYYY-MM-DD');
      setEndDate(formattedEnd);
      // エラー判定: 開始日 > 終了日
      setEndDateValid(moment(startDate).isAfter(moment(formattedEnd)));
    },
    [startDate, setEndDate, setIsError]
  );

  React.useEffect(() => {
    const today = moment();
    setMaxDate(today.format('YYYY-MM-DD'));
    setMinDate(today.add(-60, 'days').format('YYYY-MM-DD'));
  }, []);

  const resetState = useCallback(() => {
    const today = moment().add(-1, 'days').format('YYYY-MM-DD');
      setStartDate(today);
      setEndDate(today);
      setIsError(false);
      setIsOpen(false);
      setStartDateValid(false);
      setEndDateValid(false);
    },
    [setEndDate, setEndDateValid, setIsError, setIsOpen, setStartDate, setStartDateValid]
  );

  return (
    <Modal
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      open={isOpen}
      className={styles.modal}
      onClose={() => setIsOpen(false)}
    >
      <div className={styles.container}>
        <IconButton
          className={styles.closeButton}
          onClick={() => resetState()}
        >
          <Close />
        </IconButton>
        <h2 className={styles.heading}>CSVエクスポート</h2>
        <div className={styles.root}>
          <List component="nav">
            <ListItem divider={true}>
              <div className={styles.targetPeriodContainer}>
                <p className={styles.targetPeriodLabel}>開始日</p>
                <MuiPickersUtilsProvider locale={jaLocale} utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    color="primary"
                    invalidDateMessage={'有効な日付を入力してください。'}
                    id="date-picker-dialog-start"
                    label=""
                    format="yyyy/MM/dd"
                    InputProps={{ readOnly: true }}
                    minDate={minDate}
                    maxDate={maxDate}
                    value={startDate}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                    cancelLabel="キャンセル"
                    onChange={onSelectStartDate}
                    disabled={processing}
                    disableToolbar
                  />
                </MuiPickersUtilsProvider>
                {startDateValid && (
                  <p className={styles.error}>終了日より前の日付を入力してください。</p>
                )}
              </div>
              <div className={styles.targetPeriodContainer}>
                <p className={styles.targetPeriodLabel}>終了日</p>
                <MuiPickersUtilsProvider locale={jaLocale} utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    color="primary"
                    invalidDateMessage={'有効な日付を入力してください。'}
                    id="date-picker-dialog-end"
                    label=""
                    format="yyyy/MM/dd"
                    InputProps={{ readOnly: true }}
                    minDate={minDate}
                    maxDate={maxDate}
                    value={endDate}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                    cancelLabel="キャンセル"
                    onChange={onSelectEndDate}
                    disabled={processing}
                    disableToolbar
                  />
                </MuiPickersUtilsProvider>
                {endDateValid && (
                  <p className={styles.error}>開始日より後の日付を入力してください。</p>
                )}
              </div>
            </ListItem>
            <ListItem className={styles.listItem} divider={true}>
              <ListItemText>新規登録データ</ListItemText>
              <IconButton
                edge="end"
                aria-label="download"
                onClick={async e => {
                  setProcessing(true);
                  onClickNewData(e);
                }}
                disabled={!startDate || !endDate || isError || processing || startDateValid || endDateValid}
              >
                <GetApp />
              </IconButton>
            </ListItem>
            <ListItem className={styles.listItem} divider={true}>
              <ListItemText>取消・変更・解約・退去データ</ListItemText>
              <IconButton
                edge="end"
                aria-label="download"
                onClick={async e => {
                  setProcessing(true);
                  onClickModifyData(e);
                }}
                disabled={!startDate || !endDate || isError || processing || startDateValid || endDateValid}
              >
                <GetApp />
              </IconButton>
            </ListItem>
          </List>
          <AlertMessage
            open={isError}
            level={'アラート'}
            title={'エクスポートできません。'}
            message={'対象期間のCSVデータは作成されていません。'}
          />
          <div className={styles.buttonsContainer}>
            <Button
              className={styles.cancelButton}
              onClick={() => resetState()}
            >
              キャンセル
            </Button>
          </div>
        </div>
        {processing && (
          <div className={styles.progress}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Modal>
  );
};

const useAlertStyles = makeStyles(() =>
  createStyles({
    root: {
      width: 'calc(100% - 32px)',
      height: 50,
      backgroundColor: 'var(--color-error)',
      color: 'var(--color-white)',
      padding: 16,
      margin: '10px 0',
      fontSize: 14
    },
    container: {
      position: 'relative',
      top: -16,
      left: -16
    },
    icon: {
      position: 'absolute',
      top: 16,
      left: 16
    },
    title: {
      position: 'absolute',
      top: 16,
      left: 52
    },
    message: {
      position: 'absolute',
      top: 45,
      left: 52
    }
  })
);

interface P {
  open: boolean;
  level: string;
  title: string;
  message: string;
}

const AlertMessage: React.FC<P> = props => {
  const {
    open,
    //level,
    title,
    message
  } = props;

  const styles = useAlertStyles();

  return (
    <>
      {open && (
        <div className={styles.root}>
          <div className={styles.container}>
            <div className={styles.icon}>
              <Warning />
            </div>
            <div className={styles.title}>{title}</div>
            <div className={styles.message}>{message}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default CsvExportModal;
